import React from "react";
import ContentShell from "components/ContentShell";
import { SimpleTextInput } from "components/FormTextInput";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import WorldCupModeConfirmDialog from "./WorldCupModeConfirmDialog";
import { SimpleMultiSelectInput } from "components/FormMultiSelectInput";
import sessionHelperInstance from "services/sessionHelper";
import { SimpleSelectInput } from "components/FormSelectInput";
import { useSnackbar } from "hooks/useSnackbar";
import { makeStyles } from "@mui/styles";
import { Save } from "@mui/icons-material";
import {
  createAndUpdateConfig,
  getConfigurations,
  toggleWorldCupMode,
} from "services/miscService";
import SimpleSwitch from "components/Switch";
import sessionHelper from "services/sessionHelper";
import { PermittedRoles } from "utils/permissions";

const styles = makeStyles((theme) => ({
  field: {
    display: "flex",
    gap: 10,
    width: 220,
  },
  saveButton: {
    marginTop: 8,
  },
  buttonStyle: {
    padding: 0,
    marginTop: 20,
  },
  configurationCard: {
    background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
    border: "1px solid #e9ecef",
    borderRadius: "12px",
    padding: "20px",
    minWidth: "300px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
      transform: "translateY(-2px)",
    },
  },
  objectConfigCard: {
    background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
    border: "1px solid #e9ecef",
    borderRadius: "12px",
    padding: "24px",
    minWidth: "350px",
    maxWidth: "450px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
      transform: "translateY(-2px)",
    },
  },
  configHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
    paddingBottom: "12px",
    borderBottom: "2px solid #1D1030",
  },
  configTitle: {
    fontWeight: "600 !important",
    fontSize: "16px !important",
    color: "#1D1030",
    margin: "0 !important",
  },
  saveIconButton: {
    backgroundColor: "#1D1030",
    color: "white",
    padding: "8px",
    borderRadius: "8px",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "#3F3350",
      transform: "scale(1.05)",
    },
  },
  fieldContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    width: "100%",
  },
  simpleConfigContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    width: "100%",
  },
  inputFieldWrapper: {
    flex: 1,
    minWidth: "200px",
  },
  sectionTitle: {
    fontSize: "20px !important",
    fontWeight: "700 !important",
    color: "#1D1030",
    marginBottom: "24px !important",
    paddingLeft: "4px",
    borderLeft: "4px solid #1D1030",
    paddingBottom: "8px",
  },
  configGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
    gap: "24px",
    marginBottom: "32px",
  },
  objectConfigGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))",
    gap: "24px",
  },
  objectFieldsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
}));

const configurationsList = [
  {
    name: "SHOW_INITIAL_PAYTABLE",
    label: "Initial Paytable",
    type: "multiSelect", // type can be - "multiSelect" | "select" | "textInput" | "switch"
    op: "countryOptions", // can be null if not a select input
    value: "SHOW_INITIAL_PAYTABLE",
  },
  {
    name: "OPENING_CLOSING_BALANCE",
    label: "Open/Close Balance Territories",
    type: "multiSelect",
    op: "countryOptions",
    value: "OPENING_CLOSING_BALANCE",
  },
  {
    name: "MAX_BOOSTER_APPLY_LIMIT",
    label: "Maximum Booster Limit",
    type: "textInput",
    op: null,
    value: "MAX_BOOSTER_APPLY_LIMIT",
    dataType: "Number",
  },
  {
    name: "FTUE_MATCH_OVR_DIFF",
    label: "FTUE Match OVR Diff",
    type: "textInput",
    op: null,
    value: "FTUE_MATCH_OVR_DIFF",
    dataType: "Number",
  },
  {
    name: "DEFAULT_XP_PER_STAKE_UNIT",
    label: "XP Per Stake",
    type: "textInput",
    op: null,
    value: "DEFAULT_XP_PER_STAKE_UNIT",
    dataType: "Number",
  },
  {
    name: "USE_GOOGLE_ANALYTICS",
    label: "Enable Google Analytics?",
    type: "switch",
    op: null,
    value: "USE_GOOGLE_ANALYTICS",
  },
  {
    name: "HIDE_RESET_BTN",
    label: "Hide Reset Game Button?",
    type: "switch",
    op: null,
    value: "HIDE_RESET_BTN",
  },
  {
    name: "MM_GHOST_TEAM_DAYS",
    label: "Matchmaking Ghost Team Days",
    type: "textInput",
    op: null,
    value: "MM_GHOST_TEAM_DAYS",
    dataType: "Number",
    requiredPermission: PermittedRoles.CONFIGURATIONS.UPDATE_ROLES,
  },
  {
    name: "VAULT_CONFIGURATION",
    label: "Vault Configuration",
    type: "object",
    value: "VAULT_CONFIGURATION",
    objectValues: [
      {
        name: "baseXP",
        label: "Base XP",
        type: "textInput",
        op: null,
        value: "baseXP",
        dataType: "Number",
      },
      {
        name: "growthRate",
        label: "Growth Rate",
        type: "textInput",
        op: null,
        value: "growthRate",
        dataType: "Number",
      },
      {
        name: "roundOffToNearest",
        label: "Round off to Nearest",
        type: "textInput",
        op: null,
        value: "roundOffToNearest",
        dataType: "Number",
      },
      {
        name: "maxVaultLevel",
        label: "Max Vault Level",
        type: "textInput",
        op: null,
        value: "maxVaultLevel",
        dataType: "Number",
      },
      {
        name: "maxSpinCount",
        label: "Max Vault Spins",
        type: "textInput",
        op: null,
        value: "maxSpinCount",
        dataType: "Number",
      },
    ],
  },
  {
    name: "CHEAT_CONFIG",
    label: "Cheat Config",
    type: "object",
    value: "CHEAT_CONFIG",
    objectValues: [
      {
        name: "useCheat",
        label: "Allow Debug Build?",
        type: "switch",
        op: null,
        value: "useCheat",
      },
    ],
  },
  {
    name: "VOID_CHECK_DELAY_TIME",
    label: "Void Check Delay Config",
    type: "object",
    value: "VOID_CHECK_DELAY_TIME",
    objectValues: [
      {
        name: "value",
        label: "Time",
        type: "textInput",
        op: null,
        value: "value",
        dataType: "Number",
      },
      {
        name: "unit",
        label: "Unit",
        type: "select",
        op: "unitOptions",
        value: "unit",
      },
    ],
  },
  {
    name: "SEASON_POINT_CONFIG",
    label: "End of Season Points Per GBP",
    type: "object",
    value: "SEASON_POINT_CONFIG",
    objectValues: [
      {
        name: "pointForBet",
        label: "For Bet",
        type: "textInput",
        op: null,
        value: "pointForBet",
        dataType: "Number",
      },
      {
        name: "pointForIAP",
        label: "For Coin Purchase",
        type: "textInput",
        op: null,
        value: "pointForIAP",
        dataType: "Number",
      },
      {
        name: "pointForLeague",
        label: "For League Clearing",
        type: "textInput",
        op: null,
        value: "pointForLeague",
        dataType: "Number",
      },
    ],
  },
  {
    name: "QUICK_SWAP_CONFIG",
    label: "Quick Swap Configuration",
    type: "object",
    value: "QUICK_SWAP_CONFIG",
    objectValues: [
      {
        name: "ovrThreshold",
        label: "OVR Threshold",
        type: "textInput",
        op: null,
        value: "ovrThreshold",
        dataType: "Number",
      },
      {
        name: "matchFrequency",
        label: "Match Frequency",
        type: "textInput",
        op: null,
        value: "matchFrequency",
        dataType: "Number",
      },
    ],
  },
];

const DefaultValueBasedOnType = {
  switch: false,
  textInput: null,
  multiSelect: [],
  select: null,
  object: {}, // Default value for object type
};

const SaveButton = ({
  value,
  objectType = false,
  onSave = async (val?: any) => {},
}) => {
  const classes = styles();
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = React.useState(false);
  const _onSave = async () => {
    try {
      setLoading(true);
      await onSave(value);
    } catch (error) {
      showSnackbar(error?.message, "error");
    } finally {
      setLoading(false);
    }
  };
  return (
    <IconButton
      disableRipple
      onClick={_onSave}
      className={classes.saveIconButton}
      title="Save Configuration"
    >
      {loading ? (
        <CircularProgress size={20} sx={{ color: "white" }} />
      ) : (
        <Save fontSize="small" />
      )}
    </IconButton>
  );
};

const Configurations = () => {
  const classes = styles();
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = React.useState(false);
  const [wcModeEnabled, setWcModeEnabled] = React.useState(false);
  const [wcModeLoading, setWcModeLoading] = React.useState(false);
  const [wcModeDialogOpen, setWcModeDialogOpen] = React.useState(false);
  const [configDetails, setConfigDetails] = React.useState(
    configurationsList.reduce((acc, config) => {
      acc[config.value] = { data: DefaultValueBasedOnType[config.type] };
      return acc;
    }, {}),
  );
  const [options, setOptions] = React.useState({
    countryOptions: [],
    unitOptions: [
      { id: "minutes", label: "Minutes" },
      { id: "seconds", label: "Seconds" },
    ],
  });

  const onSave = async (type) => {
    try {
      const configListDet = configurationsList.find((l) => l.name === type);
      let data = configDetails[type].data;

      // Handle number and boolean fields for object type
      if (configListDet?.type === "object") {
        data = Object.entries(data).reduce((acc, [key, value]) => {
          const fieldConfig = configListDet.objectValues.find(
            (field) => field.name === key,
          );

          if (fieldConfig?.dataType === "Number") {
            acc[key] = /^\d+$/.test(String(value)) ? Number(value) : null; // Check if value contains only digits
          } else if (fieldConfig?.dataType === "Boolean") {
            acc[key] = value === "true" || value === true;
          } else {
            acc[key] = value;
          }

          return acc;
        }, {});
      } else if (configListDet?.dataType === "Number") {
        data = /^\d+$/.test(data) ? Number(data) : data; // Check if value contains only digits
      }

      const payload = { type, data };
      const response = await createAndUpdateConfig(payload);
      showSnackbar(response?.message);
    } catch (error) {
      throw error;
    }
  };

  const onChange = (e: any) => {
    const { name, value } = e?.target;

    const [configName, fieldName] = name.split(".");
    const configListDet = configurationsList.find((l) => l.name === configName);

    setConfigDetails((prev) => {
      let updatedValue = value;

      if (fieldName) {
        const fieldConfig = configListDet?.objectValues?.find(
          (field) => field.name === fieldName,
        );

        // Handle number and boolean fields
        if (fieldConfig?.dataType === "Number") {
          updatedValue = Number(value) || 0;
        } else if (fieldConfig?.dataType === "Boolean") {
          updatedValue = value === "true" || value === true || value === "on"; // Handle switch toggles
        } else if (fieldConfig?.type === "switch") {
          updatedValue = !prev[configName]?.data?.[fieldName]; // Toggle boolean for switch
        }

        updatedValue = {
          ...(prev[configName]?.data ?? {}),
          [fieldName]: updatedValue,
        };
      } else {
        if (configListDet?.type === "switch") {
          updatedValue = !prev[configName]?.data; // Toggle boolean for switch
        }
      }

      return {
        ...prev,
        [configName]: {
          ...prev[configName],
          data: updatedValue,
        },
      };
    });
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      //get all configs
      const response = await getConfigurations();

      if (response?.data?.length) {
        const _modifiedData = response?.data?.reduce((acc, curr) => {
          acc[curr.type] = { ...curr };
          return acc;
        }, {});

        setConfigDetails(_modifiedData);

        const wcConfig = response.data.find(
          (c: any) => c.type === "WORLD_CUP_MODE",
        );
        setWcModeEnabled(wcConfig?.data?.isEnabled ?? false);
      }

      setOptions((prev) => {
        return {
          ...prev,
          countryOptions: sessionHelperInstance.getCountryOptions(),
        };
      });
    } catch (error) {
      showSnackbar(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleWcModeToggleClick = () => setWcModeDialogOpen(true);

  const handleWcModeConfirm = async () => {
    setWcModeLoading(true);
    try {
      const newValue = !wcModeEnabled;
      const resp = await toggleWorldCupMode(newValue);
      setWcModeEnabled(resp?.data?.isEnabled ?? newValue);
      showSnackbar(
        `World Cup Mode ${newValue ? "enabled" : "disabled"} successfully`,
      );
      setWcModeDialogOpen(false);
    } catch (error) {
      showSnackbar(error?.message, "error");
    } finally {
      setWcModeLoading(false);
    }
  };

  const handleWcModeDialogClose = () => {
    if (!wcModeLoading) setWcModeDialogOpen(false);
  };

  React.useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ContentShell title="Configurations" loading={loading}>
      {/* World Cup Mode Section */}
      {sessionHelper.hasRole(PermittedRoles.WORLD_CUP_MODE.TOGGLE_ROLES) && (
        <>
          <Typography
            variant="h2"
            className={classes.sectionTitle}
            sx={{ mb: 3 }}
          >
            World Cup Mode
          </Typography>
          <Box mb={4}>
            <Paper
              elevation={0}
              sx={{
                background: wcModeEnabled
                  ? "linear-gradient(135deg, #fff8e1 0%, #fff3cd 100%)"
                  : "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                border: wcModeEnabled
                  ? "1px solid #f5a623"
                  : "1px solid #e9ecef",
                borderRadius: "12px",
                padding: "24px",
                maxWidth: 600,
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box display="flex" alignItems="center" gap={2}>
                  <Typography
                    variant="h3"
                    sx={{ fontSize: "18px", fontWeight: 600, color: "#1D1030" }}
                  >
                    ⚽ World Cup Mode
                  </Typography>
                  <Chip
                    label={wcModeEnabled ? "ENABLED" : "DISABLED"}
                    size="small"
                    sx={{
                      backgroundColor: wcModeEnabled ? "#4caf50" : "#9e9e9e",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "11px",
                    }}
                  />
                </Box>
                <Button
                  variant="contained"
                  onClick={handleWcModeToggleClick}
                  sx={{
                    backgroundColor: wcModeEnabled ? "#f44336" : "#f5a623",
                    fontWeight: "bold",
                    "&:hover": {
                      backgroundColor: wcModeEnabled ? "#d32f2f" : "#e0941f",
                    },
                  }}
                >
                  {wcModeEnabled ? "Disable" : "Enable"}
                </Button>
              </Box>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ mt: 1.5 }}
              >
                {wcModeEnabled
                  ? "World Cup Mode is active. New players receive national team badges and kits based on their locale."
                  : "Enable to activate the FIFA World Cup 2026 visual theme. New players will receive national team badges and kits based on their locale."}
              </Typography>
            </Paper>
          </Box>

          <WorldCupModeConfirmDialog
            open={wcModeDialogOpen}
            onClose={handleWcModeDialogClose}
            onConfirm={handleWcModeConfirm}
            isCurrentlyEnabled={wcModeEnabled}
            loading={wcModeLoading}
          />
        </>
      )}

      {/* Simple Configuration Fields */}
      <Typography variant="h2" className={classes.sectionTitle}>
        Basic Configurations
      </Typography>
      <Box className={classes.configGrid}>
        {configurationsList
          .filter(
            (i) =>
              !i.objectValues &&
              (!i.requiredPermission ||
                sessionHelper.hasRole(i.requiredPermission)),
          )
          .map(({ type, op, value, ...rest }) => {
            const renderInputField = () => {
              const commonProps = {
                ...rest,
                value: configDetails[value]?.data,
                onChange,
              };

              switch (type) {
                case "multiSelect":
                  return (
                    <Box className={classes.inputFieldWrapper}>
                      <SimpleMultiSelectInput
                        {...commonProps}
                        value={configDetails[value]?.data ?? []}
                        options={options[op]}
                      />
                    </Box>
                  );
                case "select":
                  return (
                    <Box className={classes.inputFieldWrapper}>
                      <SimpleSelectInput
                        {...commonProps}
                        value={configDetails[value]?.data ?? ""}
                        options={options[op]}
                      />
                    </Box>
                  );
                case "textInput":
                  return (
                    <Box className={classes.inputFieldWrapper}>
                      <SimpleTextInput
                        {...commonProps}
                        value={configDetails[value]?.data ?? ""}
                      />
                    </Box>
                  );
                case "switch":
                  return (
                    <Box className={classes.inputFieldWrapper}>
                      <SimpleSwitch
                        legendText={rest.label}
                        name={rest.name}
                        value={!!configDetails[value]?.data}
                        label={
                          configDetails[value]?.data ? "Enabled" : "Disabled"
                        }
                        onChange={onChange}
                      />
                    </Box>
                  );
                default:
                  return null;
              }
            };

            return (
              <Paper
                key={value}
                elevation={0}
                className={classes.configurationCard}
              >
                <Box className={classes.simpleConfigContainer}>
                  {renderInputField()}
                  <SaveButton onSave={onSave} value={value} />
                </Box>
              </Paper>
            );
          })}
      </Box>

      {/* Object Configuration Fields */}
      <Typography variant="h2" className={classes.sectionTitle}>
        Advanced Configurations
      </Typography>
      <Box className={classes.objectConfigGrid}>
        {configurationsList
          .filter(
            (i) =>
              i.objectValues &&
              (!i.requiredPermission ||
                sessionHelper.hasRole(i.requiredPermission)),
          )
          .map(({ value, op, ...rest }) => (
            <Paper
              key={value}
              elevation={0}
              className={classes.objectConfigCard}
            >
              <Box className={classes.configHeader}>
                <Typography variant="h3" className={classes.configTitle}>
                  {rest.label}
                </Typography>
                <SaveButton onSave={onSave} value={value} objectType />
              </Box>

              <Box className={classes.objectFieldsContainer}>
                {rest.objectValues?.map((item) => {
                  const name = `${value}.${item.name}`;
                  const val = configDetails[value]?.data?.[item.name];
                  const commonProps = {
                    key: item.value,
                    label: item.label,
                    name: name,
                    onChange: onChange,
                  };

                  switch (item.type) {
                    case "multiSelect":
                      return (
                        <Box
                          key={item.value}
                          className={classes.fieldContainer}
                        >
                          <SimpleMultiSelectInput
                            {...commonProps}
                            value={val ?? []}
                            options={options[op]}
                          />
                        </Box>
                      );
                    case "select":
                      return (
                        <Box
                          key={item.value}
                          className={classes.fieldContainer}
                        >
                          <SimpleSelectInput
                            {...commonProps}
                            value={val ?? ""}
                            options={options[item.op]}
                          />
                        </Box>
                      );
                    case "textInput":
                      return (
                        <Box
                          key={item.value}
                          className={classes.fieldContainer}
                        >
                          <SimpleTextInput {...commonProps} value={val ?? ""} />
                        </Box>
                      );
                    case "switch":
                      return (
                        <Box
                          key={item.value}
                          className={classes.fieldContainer}
                        >
                          <SimpleSwitch
                            {...commonProps}
                            legendText={item.label}
                            value={!!val}
                            label={val ? "Enabled" : "Disabled"}
                          />
                        </Box>
                      );
                    default:
                      return null;
                  }
                })}
              </Box>
            </Paper>
          ))}
      </Box>
    </ContentShell>
  );
};

export default Configurations;
